import { useState } from 'react';
import { useNotificationsStore } from '@/hooks/useNotificationsStore';
import type { Notification } from '@/mocks/shared';

interface Props {
  title: string;
  subtitle?: string;
  userName?: string;
  userAvatar?: string;
  notificationRole?: 'admin' | 'vendor' | 'organizer';
}

export default function DashboardTopBar({ title, subtitle, userName, userAvatar, notificationRole }: Props) {
  const [showNotifs, setShowNotifs] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotificationsStore(notificationRole ?? 'vendor');
  const shouldShowNotifications = notificationRole !== undefined;

  const notifIconMap: Record<string, string> = {
    order: 'ri-shopping-bag-line',
    order_paid: 'ri-bank-card-line',
    order_payment_started: 'ri-time-line',
    product_review: 'ri-star-smile-line',
    approval: 'ri-checkbox-circle-line',
    rejection: 'ri-close-circle-line',
    reservation: 'ri-ticket-line',
    admin_activity: 'ri-notification-4-line',
  };

  const notifColorMap: Record<string, string> = {
    order: 'text-gold bg-gold-50',
    order_paid: 'text-emerald-500 bg-emerald-50',
    order_payment_started: 'text-amber-600 bg-amber-50',
    product_review: 'text-gold-dark bg-gold-100',
    approval: 'text-navy bg-navy/10',
    rejection: 'text-red-500 bg-red-50',
    reservation: 'text-gold-dark bg-gold-100',
    admin_activity: 'text-gold-dark bg-gold-100',
  };

  const initials = userName
    ? userName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  return (
    <header className="flex min-h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-cream-dark bg-white px-4 py-3 sm:px-6">
      <div className="min-w-0">
        <h2 className="text-base font-bold text-navy">{title}</h2>
        {subtitle && <p className="truncate text-xs text-gray-400">{subtitle}</p>}
      </div>

      <div className="relative flex flex-shrink-0 items-center gap-2 sm:gap-3">
        {/* Notification Bell */}
        <button
          onClick={() => setShowNotifs((v) => !v)}
          className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-cream transition-all cursor-pointer"
        >
          <i className="ri-notification-3-line text-navy text-lg"></i>
          {shouldShowNotifications && unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {showNotifs && shouldShowNotifications && (
          <div className="absolute right-0 top-11 z-50 w-[calc(100vw-2rem)] max-w-80 overflow-hidden rounded-2xl border border-cream-dark bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
              <p className="text-sm font-bold text-navy">Notifications</p>
              <button
                onClick={markAllAsRead}
                className="text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap"
              >
                Tout marquer lu
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-cream transition-colors ${!notif.read ? 'bg-gold-50/60' : ''}`}
                  onClick={() => {
                    markAsRead(notif.id);
                    setSelectedNotification({ ...notif, read: true });
                  }}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${notifColorMap[notif.type] ?? 'text-gray-500 bg-gray-50'}`}>
                    <i className={`${notifIconMap[notif.type] ?? 'ri-notification-line'} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${!notif.read ? 'text-navy' : 'text-gray-600'}`}>{notif.title}</p>
                    <p className="text-xs text-gray-400 whitespace-pre-line break-words">{notif.message}</p>
                    <p className="text-[11px] text-gray-300 mt-0.5">{notif.time}</p>
                  </div>
                  {!notif.read && <div className="w-2 h-2 bg-gold rounded-full mt-1 flex-shrink-0"></div>}
                </div>
              ))}
              {notifications.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  Aucune notification
                </div>
              )}
            </div>
          </div>
        )}

        {selectedNotification ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedNotification(null)}>
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold-dark">Notification</p>
                  <h3 className="mt-1 text-lg font-bold text-navy">{selectedNotification.title}</h3>
                  <p className="mt-1 text-xs text-gray-400">{selectedNotification.time}</p>
                </div>
                <button type="button" onClick={() => setSelectedNotification(null)} className="rounded-xl p-2 text-gray-400 hover:bg-gray-100">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <p className="mt-5 whitespace-pre-line break-words text-sm leading-7 text-gray-600">{selectedNotification.message}</p>
              <button type="button" onClick={() => setSelectedNotification(null)} className="mt-6 w-full rounded-xl bg-navy py-3 text-sm font-bold text-white">
                Fermer
              </button>
            </div>
          </div>
        ) : null}

        {/* Séparateur */}
        <div className="w-px h-6 bg-cream-dark flex-shrink-0"></div>

        {/* User info */}
        <div className="flex items-center gap-2.5">
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userName ?? 'Utilisateur'}
              className="w-8 h-8 rounded-full object-cover border-2 border-gold/40 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-gold text-xs font-bold flex-shrink-0">
              {initials}
            </div>
          )}
          {userName && (
            <div className="hidden sm:block">
              <p className="text-xs font-bold text-navy leading-tight whitespace-nowrap">{userName}</p>
              <p className="text-[10px] text-gold leading-tight whitespace-nowrap">Connecté</p>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
